import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { isFresh } from "../../utils/helpers";
import { apiConfigs } from "../../constants/apiConfigs";
import { DMAutomationsData } from "../../types/automations/dmAutomationTypes";
import { ERROR } from "../../constants/errors";
import { fetchAvailablePosts, fetchDMAutomations as fetchDMAutomationsAPI } from "../../services/api/dmAutomations";
import { SocialAccountData } from "../../types/influencer/influencerTypes";
import { SocialPost } from "../../types/collaborations/CollaborationTypes";

export interface DMAutomationsListSlice {
    automations: Record<string, DMAutomations>
    loading: boolean,
    selectedSocialAccount: SocialAccountData | null,
    loadingAvailableToLink: boolean,
    availableToLink: Record<string, {
        items: SocialPost[],
        last_fetched_at: number | null,
        loading: boolean
    }>
}

export interface DMAutomations extends DMAutomationsData {
    page: number
    loading: boolean
    last_fetched_at: number | null
    has_more: boolean
    expired: boolean
}

export const initialState: DMAutomationsListSlice = {
    loading: false,
    loadingAvailableToLink: false,
    automations: {},
    selectedSocialAccount: null,
    availableToLink: {}
}


const STALE_TIME = apiConfigs.FETCH_DM_AUTOMATIONS.staleTime;

export const fetchDMAutomations = createAsyncThunk(
    'dmAutomations/fetchDMAutomations',
    async ({ page, socialAccountId, force = false }: { page: number; socialAccountId: string; force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { dmAutomationsList: DMAutomationsListSlice };
        if (page === 1 && !force && isFresh(state.dmAutomationsList.automations[socialAccountId]?.last_fetched_at, STALE_TIME) && !state.dmAutomationsList.automations[socialAccountId]?.expired) {
            return rejectWithValue(ERROR.CACHED);
        }
        try {
            const dmAutomations = await fetchDMAutomationsAPI(socialAccountId, page);
            return { dmAutomations, page, socialAccountId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
);

export const fetchAvailablePostsThunk = createAsyncThunk(
    'dmAutomations/fetchAvailablePosts',
    async ({ socialAccountId, force = false }: { socialAccountId: string; force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { dmAutomationsList: DMAutomationsListSlice };
        if (!force && isFresh(state.dmAutomationsList.availableToLink[socialAccountId]?.last_fetched_at, STALE_TIME)) {
            return rejectWithValue(ERROR.CACHED);
        }
        try {
            const posts = await fetchAvailablePosts(socialAccountId);
            return { posts, socialAccountId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
);

const dmAutomationsListSlice = createSlice({
    name: 'dmAutomations',
    initialState,
    reducers: {
        markExpired: (state) => {
            // state.expired = true
        },
        setSelectedSocialAccount: (state, {payload}) => {
            state.selectedSocialAccount = payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDMAutomations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDMAutomations.fulfilled, (state, action) => {
                const socialAccountId = action.payload.socialAccountId;
                if (!state.automations[socialAccountId]) {
                    state.automations[socialAccountId] = {
                        items: [],
                        count: 0,
                        cta_url: '',
                        trigger_list: [],
                        button_text_list: [],
                        page: 1,
                        loading: false,
                        last_fetched_at: null,
                        has_more: false,
                        expired: false
                    }
                }
                const dmAutomationState = state.automations[socialAccountId];
                const stateCopy = { ...dmAutomationState };
                Object.assign(state.automations[socialAccountId], stateCopy);
                const stateForUpdate = state.automations[socialAccountId];
                stateForUpdate.items = action.payload.page === 1 ?
                    action.payload.dmAutomations.items :
                    [...stateForUpdate.items, ...action.payload.dmAutomations.items];

                stateForUpdate.count = action.payload.dmAutomations.count;
                if(action.payload.dmAutomations.cta_url){
                    stateForUpdate.cta_url = action.payload.dmAutomations.cta_url;
                }
                stateForUpdate.trigger_list = Array.isArray(action.payload.dmAutomations?.trigger_list) ? 
                    action.payload.dmAutomations.trigger_list : [];
                stateForUpdate.button_text_list = Array.isArray(action.payload.dmAutomations?.button_text_list) ? 
                    action.payload.dmAutomations.button_text_list : [];
                    
                stateForUpdate.page = action.payload.page;
                stateForUpdate.loading = false;
                stateForUpdate.has_more = action.payload.dmAutomations.count > stateForUpdate.items.length;
                stateForUpdate.last_fetched_at = Date.now();
                stateForUpdate.expired = false;
            })
            .addCase(fetchDMAutomations.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchAvailablePostsThunk.fulfilled, (state, {payload}) => {
                const socialAccountId = payload.socialAccountId;
                if (!state.availableToLink[socialAccountId]) {
                    state.availableToLink[socialAccountId] = {
                        items: [],
                        last_fetched_at: null,
                        loading: false
                    }
                }
                state.availableToLink[socialAccountId].items = payload.posts;
                state.availableToLink[socialAccountId].last_fetched_at = Date.now()
                state.availableToLink[socialAccountId].loading = false;
                state.loadingAvailableToLink = false;
            })
            .addCase(fetchAvailablePostsThunk.pending, (state) => {
                state.loadingAvailableToLink = true;
            })
            .addCase(fetchAvailablePostsThunk.rejected, (state) => {
                state.loadingAvailableToLink = false;
            })
    }
})

export const { markExpired, setSelectedSocialAccount } = dmAutomationsListSlice.actions;

export default dmAutomationsListSlice.reducer;